import { useState } from 'react'
import {
  submitSupportRequest,
  type MobileDeviceInfo,
} from '@/lib/support'

interface FormData {
  name: string
  email: string
  phone: string
  issue: string
}

interface FormErrors {
  name?: string
  email?: string
  issue?: string
}

interface SupportFormProps {
  deviceInfo?: MobileDeviceInfo
}

const inputBase: React.CSSProperties = {
  width: '100%',
  background: '#FFFFFF',
  border: '1px solid #E5E7EB',
  borderRadius: '12px',
  padding: '14px 16px 14px 46px',
  fontSize: '15px',
  color: '#111827',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
}

export function SupportForm({ deviceInfo }: SupportFormProps) {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    issue: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')
  const [honeypot, setHoneypot] = useState('')

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required'
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.issue.trim()) {
      newErrors.issue = 'Please describe your issue'
    } else if (formData.issue.trim().length < 10) {
      newErrors.issue = 'Message must be at least 10 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
    if (submitStatus !== 'idle') setSubmitStatus('idle')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Honeypot — silently succeed for bots without sending anything.
    if (honeypot) {
      setSubmitStatus('success')
      setSubmitMessage('Thank you! Your message has been sent successfully.')
      return
    }

    if (!validateForm()) return

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      await submitSupportRequest({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        issue: formData.issue,
        deviceInfo,
      })
      setSubmitStatus('success')
      setSubmitMessage('Thank you! Your message has been sent successfully.')
      setFormData({ name: '', email: '', phone: '', issue: '' })
    } catch {
      setSubmitStatus('error')
      setSubmitMessage('Failed to send message. Please try again later.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const focusRing = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = '#3B82F6'
    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)'
  }
  const blurRing =
    (hasError: boolean) =>
    (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      e.currentTarget.style.borderColor = hasError ? '#EF4444' : '#E5E7EB'
      e.currentTarget.style.boxShadow = 'none'
    }

  return (
    <form
      onSubmit={handleSubmit}
      className="support-form"
      style={{
        background: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '24px',
        boxShadow: '0 24px 64px rgba(59,130,246,0.08)',
      }}
    >
      {/* Honeypot — hidden from users, visible to bots */}
      <input
        type="text"
        name="website"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      {/* Full Name */}
      <Field label="Full Name" required gridSpan={1}>
        <IconWrap>
          <UserIcon />
        </IconWrap>
        <input
          id="name"
          placeholder="John Doe"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          onFocus={focusRing}
          onBlur={blurRing(Boolean(errors.name))}
          style={{ ...inputBase, borderColor: errors.name ? '#EF4444' : '#E5E7EB' }}
        />
        <ErrorText message={errors.name} />
      </Field>

      {/* Email */}
      <Field label="Email Address" required gridSpan={1}>
        <IconWrap>
          <MailIcon />
        </IconWrap>
        <input
          id="email"
          type="email"
          placeholder="john@example.com"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          onFocus={focusRing}
          onBlur={blurRing(Boolean(errors.email))}
          style={{ ...inputBase, borderColor: errors.email ? '#EF4444' : '#E5E7EB' }}
        />
        <ErrorText message={errors.email} />
      </Field>

      {/* Phone */}
      <Field label="Phone Number" optional gridSpan={2}>
        <IconWrap>
          <PhoneIcon />
        </IconWrap>
        <input
          id="phone"
          type="tel"
          placeholder="+1 (555) 000-0000"
          value={formData.phone}
          onChange={(e) => handleChange('phone', e.target.value)}
          onFocus={focusRing}
          onBlur={blurRing(false)}
          style={inputBase}
        />
      </Field>

      {/* Issue */}
      <Field label="Write Your Issue" required gridSpan={2}>
        <IconWrap top={16}>
          <MessageIcon />
        </IconWrap>
        <textarea
          id="issue"
          placeholder="Describe the issue you're experiencing..."
          value={formData.issue}
          onChange={(e) => handleChange('issue', e.target.value)}
          onFocus={focusRing}
          onBlur={blurRing(Boolean(errors.issue))}
          style={{
            ...inputBase,
            minHeight: '150px',
            paddingTop: '14px',
            resize: 'vertical',
            borderColor: errors.issue ? '#EF4444' : '#E5E7EB',
          }}
        />
        <ErrorText message={errors.issue} />
      </Field>

      {/* Status message */}
      {submitStatus !== 'idle' && (
        <div style={{ gridColumn: '1 / -1' }}>
          <div
            style={{
              padding: '14px 16px',
              borderRadius: '12px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: submitStatus === 'success' ? '#ECFDF5' : '#FEF2F2',
              border: `1px solid ${submitStatus === 'success' ? '#A7F3D0' : '#FECACA'}`,
              color: submitStatus === 'success' ? '#047857' : '#B91C1C',
            }}
          >
            {submitStatus === 'success' ? <CheckIcon /> : <AlertIcon />}
            <span>{submitMessage}</span>
          </div>
        </div>
      )}

      {/* Submit */}
      <div style={{ gridColumn: '1 / -1', marginTop: '4px' }}>
        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            width: '100%',
            border: 'none',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            background: 'linear-gradient(135deg, #3B82F6, #6366F1)',
            color: 'white',
            fontSize: '16px',
            fontWeight: '700',
            fontFamily: 'inherit',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
            opacity: isSubmitting ? 0.65 : 1,
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
          }}
          onMouseEnter={(e) => {
            if (isSubmitting) return
            e.currentTarget.style.transform = 'translateY(-1px)'
            e.currentTarget.style.boxShadow = '0 12px 30px rgba(59,130,246,0.4)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)'
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(59,130,246,0.3)'
          }}
        >
          {isSubmitting ? 'Sending…' : 'Send Message'}
          {!isSubmitting && <SendIcon />}
        </button>
      </div>
    </form>
  )
}

/* ---------- Layout helpers ---------- */

function Field({
  label,
  required,
  optional,
  gridSpan,
  children,
}: {
  label: string
  required?: boolean
  optional?: boolean
  gridSpan: 1 | 2
  children: React.ReactNode
}) {
  return (
    <div
      style={{
        gridColumn: gridSpan === 2 ? '1 / -1' : 'auto',
        position: 'relative',
        minWidth: 0,
      }}
    >
      <label
        style={{
          display: 'block',
          fontSize: '14px',
          fontWeight: '600',
          color: '#111827',
          marginBottom: '8px',
        }}
      >
        {label}{' '}
        {required && <span style={{ color: '#3B82F6' }}>*</span>}
        {optional && (
          <span style={{ color: '#9CA3AF', fontWeight: '400', fontSize: '13px' }}>
            (Optional)
          </span>
        )}
      </label>
      <div style={{ position: 'relative' }}>{children}</div>
    </div>
  )
}

function IconWrap({ children, top }: { children: React.ReactNode; top?: number }) {
  return (
    <span
      style={{
        position: 'absolute',
        left: '16px',
        top: top !== undefined ? `${top}px` : '50%',
        transform: top !== undefined ? 'none' : 'translateY(-50%)',
        color: '#9CA3AF',
        display: 'flex',
        pointerEvents: 'none',
      }}
    >
      {children}
    </span>
  )
}

function ErrorText({ message }: { message?: string }) {
  if (!message) return null
  return (
    <p
      style={{
        color: '#EF4444',
        fontSize: '13px',
        margin: '6px 0 0 2px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      }}
    >
      <AlertIcon size={14} /> {message}
    </p>
  )
}

/* ---------- Inline icons ---------- */

const iconProps = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
})

function UserIcon() {
  return (
    <svg {...iconProps(18)}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}
function MailIcon() {
  return (
    <svg {...iconProps(18)}>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  )
}
function PhoneIcon() {
  return (
    <svg {...iconProps(18)}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
    </svg>
  )
}
function MessageIcon() {
  return (
    <svg {...iconProps(18)}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
    </svg>
  )
}
function SendIcon() {
  return (
    <svg {...iconProps(18)}>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  )
}
function CheckIcon() {
  return (
    <svg {...iconProps(18)}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="m22 4-10 10-3-3" />
    </svg>
  )
}
function AlertIcon({ size = 18 }: { size?: number }) {
  return (
    <svg {...iconProps(size)}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 8v4M12 16h.01" />
    </svg>
  )
}
